import type { ModuleSession } from "../hooks/useProethicaSessionStore";

type LiveExamStatus = "NOT_STARTED" | "ACTIVE" | "COMPLETE" | (string & {});

export type LiveExamSignal = {
	collectionId?: number;
	status?: LiveExamStatus;
	numericId?: number | null;
};

const FRESH_TRACKED_SESSION_GRACE_MS = 60_000;

export const resolveStaleTrackedModuleIds = (
	sessionByModule: Record<string, ModuleSession>,
	exams: LiveExamSignal[],
	dataUpdatedAt: number,
	now = Date.now(),
): string[] => {
	const examByCollectionId = new Map(
		exams.map((exam) => [String(exam.collectionId), exam]),
	);
	const staleModuleIds: string[] = [];
	for (const moduleId of Object.keys(sessionByModule)) {
		const exam = examByCollectionId.get(moduleId);
		if (!exam) {
			continue;
		}
		const tracked = sessionByModule[moduleId];
		const staleStatus = exam.status === "NOT_STARTED";
		const staleNumericId =
			exam.numericId != null && exam.numericId !== tracked.numericId;
		const isAuthoritative = dataUpdatedAt > (tracked.trackedAt ?? 0);
		const trackedAt = tracked.trackedAt ?? 0;
		const canTrustNotStarted =
			tracked.trackedAt == null ||
			now - trackedAt >= FRESH_TRACKED_SESSION_GRACE_MS;
		if (
			isAuthoritative &&
			(staleNumericId || (staleStatus && canTrustNotStarted))
		) {
			staleModuleIds.push(moduleId);
		}
	}
	return staleModuleIds;
};

export type AdoptableModuleSession = {
	moduleId: string;
	numericId: number;
};

// An ACTIVE exam row with a numericId is an in-progress session the
// backend knows about - adopting it lets resume work on a device that never
// created it, instead of minting a duplicate session. A fresh (non-stale)
// locally tracked session wins: right after a local create the distribution
// row can still point at an older session.
export const resolveAdoptableModuleSessions = (
	sessionByModule: Record<string, ModuleSession>,
	exams: LiveExamSignal[],
	staleModuleIds: string[],
): AdoptableModuleSession[] => {
	const stale = new Set(staleModuleIds);
	const adoptable: AdoptableModuleSession[] = [];
	for (const exam of exams) {
		if (exam.collectionId == null || exam.status !== "ACTIVE") {
			continue;
		}
		const numericId = exam.numericId;
		if (numericId == null || numericId <= 0) {
			continue;
		}
		const moduleId = String(exam.collectionId);
		if (sessionByModule[moduleId] && !stale.has(moduleId)) {
			continue;
		}
		adoptable.push({ moduleId, numericId });
	}
	return adoptable;
};
